import { LanguageSelector, theme } from '@arnold/common';
import { ReactComponent as LogoPrimary } from '@arnold/common/lib/assets/logo/LogoPrimaryDarkBkg.svg';
import styled from '@emotion/styled/macro';
import { History } from 'history';
import { ReactElement, useEffect } from 'react';
import { allowedLanguages } from '../../lib/common';
import i18n from '../../translations/i18n';
import storage from '../../lib/storage';

const PublicLayoutStyled = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${theme.background.primary};
  @media (min-width: ${theme.breakpoint.small}) {
    flex-direction: row;
  }
`;

export const Aside = styled.div`
  background-color: ${theme.colors.brand.primary};
  padding: 0 ${theme.spacing.f};
  display: flex;
  justify-content: center;
  flex-direction: column;
  @media (min-width: ${theme.breakpoint.small}) {
    min-height: 100%;
    flex: 1;
    width: auto;
  }
`;

const Content = styled.div`
  display: flex;
  flex-grow: 1;
  padding: ${theme.spacing.h} ${theme.spacing.f} 96px ${theme.spacing.f};
  @media (min-width: ${theme.breakpoint.small}) {
    height: 100%;
    min-height: 100vh;
    flex: 1;
    padding: ${theme.spacing.k} ${theme.spacing.f} 96px ${theme.spacing.f};
  }
  @media (min-width: ${theme.breakpoint.large}) {
    padding: ${theme.spacing.l} ${theme.spacing.f};
  }
`;

const LanguageSelectorStyled = styled.div`
  display: block;
  position: absolute;
  top: ${theme.spacing.f};
  right: ${theme.spacing.f};

  svg path {
    fill: ${theme.colors.actionPrimary.default};
  }

  @media (max-width: ${theme.breakpoint.small}) {
    display: none;
  }
`;

const MobileLanguageSelector = styled(LanguageSelectorStyled)`
  display: block !important;
  top: -${theme.spacing.f};
  right: 0;
  @media (min-width: ${theme.breakpoint.small}) {
    display: none !important;
  }
`;

const ContentContainer = styled.div`
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  @media (min-width: ${theme.breakpoint.small}) {
    width: 304px;
  }
  @media (min-width: ${theme.breakpoint.medium}) {
    width: 352px;
  }

  @media (min-width: ${theme.breakpoint.large}) {
    width: 464px;
  }
`;

const ContentForm = styled.div`
  flex: 1;
`;
interface IProps {
  basePath: string;
  children: ReactElement;
  aside?: ReactElement;
  lang?: string;
  className?: string;
  history: History;
}

const handleLanguageChange = (history: History, basePath: string, lang: string) => {
  const search = history.location.search;
  history.push({
    pathname: `/${basePath}/${lang}`,
    search,
  });
};

const PublicLayout = ({ basePath, lang, aside, history, children, className }: IProps) => {
  useEffect(() => {
    if (lang) {
      storage.setDetectedLanguage(lang);
      i18n.changeLanguage(lang);
    }
  }, [lang]);

  return (
    <div data-cy="admin-page" className={className}>
      <LanguageSelectorStyled data-id="lang-selector">
        <LanguageSelector
          onChange={(lang) => handleLanguageChange(history, basePath, lang)}
          lang={lang}
          allowedLanguages={allowedLanguages}
        />
      </LanguageSelectorStyled>
      <PublicLayoutStyled>
        <Aside>{aside ? aside : <LogoPrimary />}</Aside>
        <Content>
          <ContentContainer>
            <ContentForm>
              <div className="position-relative pt-8">
                <MobileLanguageSelector>
                  <LanguageSelector
                    onChange={(lng) => handleLanguageChange(history, basePath, lng)}
                    lang={lang}
                    allowedLanguages={allowedLanguages}
                  />
                </MobileLanguageSelector>
                {children}
              </div>
            </ContentForm>
          </ContentContainer>
        </Content>
      </PublicLayoutStyled>
    </div>
  );
};

export default PublicLayout;
